import {
  CloseOutlined,
  EllipsisOutlined,
  ExperimentOutlined,
  LoginOutlined,
  MessageOutlined,
  PlusOutlined,
  ReloadOutlined,
  SlidersOutlined,
} from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Popover,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import {
  api,
  type EventSpeakerWithProfile,
  type SlotRow,
  type SpeakerRole,
} from '../lib/api';
import { openSpeakerPreview } from '../lib/preview';
import type { PresenceInfo } from '../scenes/EventScene';
import { Hand } from './Hand';
import { PresenceDot } from './PresenceDot';
import { RoleSelector } from './RoleSelector';
import {
  SendMessageModal,
  sendMessageToSpeaker,
} from './SendMessageModal';
import { SlideControlPanel } from './SlideControlPanel';

const { Text } = Typography;

export type WaitingRoomRow = {
  key: string;
  presence: PresenceInfo;
  name: string;
  email: string;
  eventSpeaker: EventSpeakerWithProfile;
};

export function WaitingRoomList({
  speakers,
  slots,
  presence,
  onChange,
  onSendInvite,
}: {
  speakers: EventSpeakerWithProfile[];
  slots: SlotRow[];
  presence: Record<string, PresenceInfo>;
  onChange: () => Promise<void> | void;
  onSendInvite: (speakerId: string) => Promise<void>;
}) {
  const [slidePanelFor, setSlidePanelFor] = useState<EventSpeakerWithProfile | null>(null);
  const [messageFor, setMessageFor] = useState<EventSpeakerWithProfile | null>(null);
  const rows: WaitingRoomRow[] = speakers
    .map((es) => ({
      key: es.id,
      presence: presence[es.speaker_id] ?? { status: 'offline' as const },
      name: es.name,
      email: es.email,
      eventSpeaker: es,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const columns: ColumnsType<WaitingRoomRow> = [
    {
      title: 'Waiting room',
      dataIndex: 'name',
      key: 'name',
      render: (_text, row) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            minWidth: 0,
          }}
        >
          <div style={{ paddingTop: 4, flexShrink: 0 }}>
            <PresenceDot presence={row.presence} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minWidth: 0,
              }}
            >
              <Text
                strong
                ellipsis
                style={{ fontSize: 14, minWidth: 0, flex: 1 }}
              >
                {row.name}
              </Text>
              {row.eventSpeaker.is_hand_raised === 1 && (
                <Hand
                  size={18}
                  title="Click to lower hand"
                  onClick={async () => {
                    await api(
                      `/api/events/${row.eventSpeaker.event_id}/speakers/${row.eventSpeaker.id}`,
                      {
                        method: 'PATCH',
                        body: JSON.stringify({ is_hand_raised: false }),
                      },
                    );
                    await onChange();
                  }}
                />
              )}
            </div>
            <div style={{ marginTop: 2 }}>
              <RoleSelector
                value={row.eventSpeaker.role}
                onChange={async (role: SpeakerRole) => {
                  await api(
                    `/api/events/${row.eventSpeaker.event_id}/speakers/${row.eventSpeaker.id}`,
                    {
                      method: 'PATCH',
                      body: JSON.stringify({ role }),
                    },
                  );
                  await onChange();
                }}
              />
            </div>
            <Text
              type="secondary"
              ellipsis
              style={{ display: 'block', fontSize: 11 }}
            >
              {row.email}
            </Text>
            <NoteCell
              value={row.eventSpeaker.note ?? ''}
              onSave={async (note) => {
                await api(
                  `/api/events/${row.eventSpeaker.event_id}/speakers/${row.eventSpeaker.id}`,
                  {
                    method: 'PATCH',
                    body: JSON.stringify({ note: note || null }),
                  },
                );
                await onChange();
              }}
            />
          </div>
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      fixed: 'right' as const,
      render: (_text, row) => (
        <WaitingRoomActions
          row={row}
          slots={slots}
          onChange={onChange}
          onSendInvite={onSendInvite}
          onOpenSlideControl={() => setSlidePanelFor(row.eventSpeaker)}
          onSendMessage={() => setMessageFor(row.eventSpeaker)}
        />
      ),
    },
  ];

  return (
    <>
    <Table
      rowKey="key"
      dataSource={rows}
      columns={columns}
      pagination={false}
      locale={{ emptyText: 'Waiting room is empty.' }}
      scroll={{ x: 320 }}
    />
    <SlideControlPanel
      open={slidePanelFor !== null}
      speakerName={slidePanelFor?.name ?? ''}
      eventSpeaker={
        slidePanelFor ?? {
          id: '',
          event_id: '',
          slide_license_id: null,
          slide_license_name: null,
          slide_license_key: null,
          slide_control_enabled: 0,
        }
      }
      onClose={() => setSlidePanelFor(null)}
      onChange={onChange}
    />
    <SendMessageModal
      open={messageFor !== null}
      recipient={messageFor?.name ?? ''}
      onSend={async (payload) => {
        if (messageFor) {
          await sendMessageToSpeaker(messageFor.event_id, messageFor.id, payload);
        }
      }}
      onClose={() => setMessageFor(null)}
    />
    </>
  );
}

function WaitingRoomActions({
  row,
  slots,
  onChange,
  onSendInvite,
  onOpenSlideControl,
  onSendMessage,
}: {
  row: WaitingRoomRow;
  slots: SlotRow[];
  onChange: () => Promise<void> | void;
  onSendInvite: (speakerId: string) => Promise<void>;
  onOpenSlideControl: () => void;
  onSendMessage: () => void;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [form] = Form.useForm<{ slotId: string }>();

  const emptySlots = slots.filter((s) => !s.speaker_id);
  const filledSlots = slots.filter((s) => s.speaker_id);

  const submitAssign = async ({ slotId }: { slotId: string }) => {
    const slot = slots.find((s) => s.id === slotId);
    if (!slot) return;
    if (!slot.vmix_id) {
      const vMixId = window.prompt(`Slot ${slot.order_index + 1} needs a vMix ID:`);
      if (!vMixId) return;
      await api(`/api/slots/${slotId}/assign`, {
        method: 'POST',
        body: JSON.stringify({
          speaker_id: row.eventSpeaker.speaker_id,
          vmix_id: vMixId,
        }),
      });
    } else {
      await api(`/api/slots/${slotId}/assign`, {
        method: 'POST',
        body: JSON.stringify({
          speaker_id: row.eventSpeaker.speaker_id,
          vmix_id: slot.vmix_id,
        }),
      });
    }
    setPopoverOpen(false);
    form.resetFields();
    await onChange();
  };

  const slideOn = row.eventSpeaker.slide_control_enabled === 1;

  const overflowItems: MenuProps['items'] = [
    {
      key: 'message',
      label: 'Send message',
      icon: <MessageOutlined />,
      onClick: onSendMessage,
    },
    {
      key: 'slide',
      label: slideOn ? 'Slide control (on)' : 'Slide control',
      icon: <SlidersOutlined style={{ color: slideOn ? '#428e3e' : undefined }} />,
      onClick: onOpenSlideControl,
    },
    {
      key: 'preview',
      label: 'Preview as this speaker',
      icon: <ExperimentOutlined />,
      onClick: async () => {
        try {
          await openSpeakerPreview(row.eventSpeaker.speaker_id, {
            eventId: row.eventSpeaker.event_id,
            target: 'waiting',
          });
        } catch (err) {
          Modal.error({
            title: 'Could not open preview',
            content: (err as Error).message,
          });
        }
      },
    },
    {
      key: 'refresh',
      label: 'Refresh browser',
      icon: <ReloadOutlined />,
      onClick: () => {
        Modal.confirm({
          title: "Refresh this speaker's browser?",
          okText: 'Refresh',
          onOk: async () => {
            await api(
              `/api/events/${row.eventSpeaker.event_id}/speakers/${row.eventSpeaker.id}/refresh`,
              { method: 'POST' },
            );
          },
        });
      },
    },
    { type: 'divider' },
    {
      key: 'remove',
      label: 'Remove from event',
      icon: <CloseOutlined />,
      danger: true,
      onClick: () => {
        Modal.confirm({
          title: 'Remove from event?',
          okText: 'Remove',
          okButtonProps: { danger: true },
          onOk: async () => {
            await api(
              `/api/events/${row.eventSpeaker.event_id}/speakers/${row.eventSpeaker.id}`,
              { method: 'DELETE' },
            );
            await onChange();
          },
        });
      },
    },
  ];

  return (
    <Space size="small" align="center" style={{ float: 'right' }}>
      <Button size="small" type="link" onClick={() => onSendInvite(row.eventSpeaker.speaker_id)}>
        Invite
      </Button>
      <Popover
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        trigger="click"
        title="Assign to slot"
        content={
          <Form form={form} onFinish={submitAssign}>
            <Space>
              <Form.Item
                name="slotId"
                noStyle
                rules={[{ required: true, message: 'Pick a slot' }]}
              >
                <Select
                  showSearch
                  placeholder="Slot"
                  style={{ width: 120 }}
                  options={[
                    {
                      label: 'Empty',
                      options: emptySlots.map((s) => ({
                        label: `Slot ${s.order_index + 1}`,
                        value: s.id,
                      })),
                    },
                    {
                      label: 'In use',
                      options: filledSlots.map((s) => ({
                        label: `Slot ${s.order_index + 1}`,
                        value: s.id,
                      })),
                    },
                  ]}
                />
              </Form.Item>
              <Button htmlType="submit" type="primary">
                Assign
              </Button>
            </Space>
          </Form>
        }
      >
        <Button
          size="small"
          shape="circle"
          title="Assign to slot"
          icon={<LoginOutlined style={{ color: '#999' }} />}
        />
      </Popover>
      <Dropdown menu={{ items: overflowItems }} trigger={['click']} placement="bottomRight">
        <Button
          size="small"
          shape="circle"
          icon={<EllipsisOutlined style={{ color: '#999' }} />}
          title="More"
        />
      </Dropdown>
    </Space>
  );
}

export function EmptyWaitingRoom() {
  return (
    <Space>
      <PlusOutlined /> Add speakers from the panel below.
    </Space>
  );
}

function NoteCell({
  value,
  onSave,
}: {
  value: string;
  onSave: (note: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <Text
        type="secondary"
        style={{
          display: 'block',
          fontSize: 12,
          cursor: 'pointer',
          fontStyle: value ? undefined : 'italic',
        }}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        title="Click to edit note"
      >
        {value || 'Add note…'}
      </Text>
    );
  }

  return (
    <Input.TextArea
      autoFocus
      size="small"
      autoSize={{ minRows: 1, maxRows: 4 }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={async () => {
        if (draft !== value) await onSave(draft);
        setEditing(false);
      }}
      onPressEnter={async (e) => {
        if (!e.shiftKey) {
          e.preventDefault();
          if (draft !== value) await onSave(draft);
          setEditing(false);
        }
      }}
      style={{ marginTop: 4 }}
    />
  );
}
